import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { configAPI } from '../services/api';
import ImageUpload from '../components/ImageUpload';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

const Configuration = () => {
  const [activeTab, setActiveTab] = useState('splash');
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(false);
  const [splashLogo, setSplashLogo] = useState('');
  const [appLogo, setAppLogo] = useState('');
  const [bannerImage, setBannerImage] = useState('');
  const [forceUpdate, setForceUpdate] = useState({ version: '', message: '', isActive: false });
  const [forceUpdateLoading, setForceUpdateLoading] = useState(false);

  // Add state for legal fields
  const [legal, setLegal] = useState({
    termsConditions: '',
    privacyPolicy: '',
    dataSafety: '',
    disclaimer: '',
  });

  useEffect(() => {
    fetchConfig();
  }, []);

  useEffect(() => {
    if (config && config.forceUpdate) {
      setForceUpdate({
        version: config.forceUpdate.version || '',
        message: config.forceUpdate.message || '',
        isActive: config.forceUpdate.isActive || false,
        androidUrl: config.forceUpdate.androidUrl || '',
        iosUrl: config.forceUpdate.iosUrl || ''
      });
    }
  }, [config]);

  // Always sync legal state with config.legal for the four fields
  useEffect(() => {
    if (config && config.legal) {
      setLegal(l => ({
        ...l,
        termsConditions: config.legal.termsConditions || '',
        privacyPolicy: config.legal.privacyPolicy || '',
        dataSafety: config.legal.dataSafety || '',
        disclaimer: config.legal.disclaimer || '',
      }));
    }
  }, [config?.legal?.termsConditions, config?.legal?.privacyPolicy, config?.legal?.dataSafety, config?.legal?.disclaimer]);

  const fetchConfig = async () => {
    try {
      setLoading(true);
      const response = await configAPI.getConfig();
      const data = response.data || {};

      // Map root-level legal fields into a legal object for the UI
      const legal = {
        termsConditions: data.termsAndConditions || '',
        privacyPolicy: data.privacyPolicy || '',
        dataSafety: data.dataSafety || '',
        disclaimer: data.disclaimer || '',
      };

      setConfig({
        ...data,
        legal,
      });

      // Set images as before
      if (data?.splash?.logo) setSplashLogo(data.splash.logo);
      if (data?.login?.bannerImage) setBannerImage(data.login.bannerImage);
    } catch (error) {
      console.error('Error fetching config:', error);
      toast.error('Failed to load configuration');
      // Set default config
      setConfig({
        splash: {
          logo: '',
          appName: 'Chitti Finserv',
          tagline: 'Your Trusted Financial Partner',
          backgroundColor: '#3B82F6',
          textColor: '#FFFFFF',
          duration: 3
        },
        login: {
          bannerImage: '',
          welcomeTitle: 'Welcome to Chitti Finserv',
          welcomeSubtitle: 'Your trusted financial partner',
          enableOTP: true,
          enableRegistration: true,
          enableForgotPassword: true,
          enableBiometric: true,
          termsText: 'By continuing, you agree to our Terms & Conditions'
        },
        contactInfo: {
          companyName: 'Chitti Finserv',
          email: 'info@chittifinserv.com',
          phone: '+91 9876543210',
          whatsapp: '+91 9876543210',
          address: 'Mumbai, Maharashtra, India',
          workingHours: 'Mon-Fri: 9AM-6PM',
          website: 'https://chittifinserv.com',
          socialMedia: {
            facebook: '',
            instagram: '',
            twitter: '',
            linkedin: ''
          }
        },
        legal: {
          termsConditions: '',
          privacyPolicy: '',
          dataSafety: '',
          disclaimer: ''
        },
        sms: {
          provider: 'bulksms',
          apiKey: '',
          apiSecret: '',
          senderId: 'CHITTI',
          templateId: ''
        },
        dlt: {
          templates: []
        }
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSplashSubmit = async (e) => {
    e.preventDefault();
    try {
      const formData = new FormData(e.target);
      const data = {
        logo: splashLogo || formData.get('logo'),
        appName: formData.get('appName'),
        tagline: formData.get('tagline'),
        backgroundColor: formData.get('backgroundColor'),
        textColor: formData.get('textColor'),
        duration: parseInt(formData.get('duration')) || 3,
      };
      
      await configAPI.updateSplashConfig(data);
      toast.success('Splash configuration updated successfully');
      fetchConfig();
    } catch (error) {
      console.error('Error updating splash config:', error);
      toast.error('Failed to update splash configuration');
    }
  };

  const handleLoginSubmit = async (e) => {
    e.preventDefault();
    try {
      const formData = new FormData(e.target);
      const data = {
        bannerImage: bannerImage || formData.get('bannerImage'),
        welcomeTitle: formData.get('welcomeTitle'),
        welcomeSubtitle: formData.get('welcomeSubtitle'),
        enableOTP: formData.get('enableOTP') === 'true',
        enableRegistration: formData.get('enableRegistration') === 'true',
        enableForgotPassword: formData.get('enableForgotPassword') === 'true',
        enableBiometric: formData.get('enableBiometric') === 'true',
        termsText: formData.get('termsText'),
      };
      
      await configAPI.updateLoginConfig(data);
      toast.success('Login configuration updated successfully');
      fetchConfig();
    } catch (error) {
      console.error('Error updating login config:', error);
      toast.error('Failed to update login configuration');
    }
  };

  const handleLoanFormSubmit = (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const requiredFields = [];
    ['name', 'email', 'phone', 'address', 'income', 'employment', 'loanAmount', 'purpose'].forEach(field => {
      if (formData.get(`required_${field}`)) {
        requiredFields.push(field);
      }
    });
    
    const data = {
      formTitle: formData.get('formTitle'),
      formSubtitle: formData.get('formSubtitle'),
      enableDocuments: formData.get('enableDocuments') === 'true',
      enableTerms: formData.get('enableTerms') === 'true',
      requiredFields: requiredFields,
      categories: formData.get('categories'),
    };
    // Removed updateLoanFormMutation call
    toast.info('Loan form update not implemented');
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    try {
      const formData = new FormData(e.target);
      const socialMedia = {
        facebook: formData.get('facebook'),
        instagram: formData.get('instagram'),
        twitter: formData.get('twitter'),
        linkedin: formData.get('linkedin'),
      };
      
      // Remove empty social media fields to avoid validation errors
      Object.keys(socialMedia).forEach(key => {
        if (!socialMedia[key]) {
          delete socialMedia[key];
        }
      });
      
      const data = {
        companyName: formData.get('companyName'),
        email: formData.get('email'),
        phone: formData.get('phone'),
        whatsapp: formData.get('whatsapp'),
        address: formData.get('address'),
        workingHours: formData.get('workingHours'),
        website: formData.get('website'),
        socialMedia,
      };
      
      await configAPI.updateContactConfig(data);
      toast.success('Contact configuration updated successfully');
      fetchConfig();
    } catch (error) {
      console.error('Error updating contact config:', error);
      toast.error('Failed to update contact configuration');
    }
  };

  // Update handleLegalSubmit to use legal state
  const handleLegalSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = { ...legal };
      await configAPI.updateLegalConfig(data);
      toast.success('Legal pages updated successfully');
      fetchConfig();
    } catch (error) {
      console.error('Error updating legal config:', error);
      toast.error('Failed to update legal pages');
    }
  };

  const handleNotificationSubmit = (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const data = {
      emailNotifications: formData.get('emailNotifications') === 'true',
      smsNotifications: formData.get('smsNotifications') === 'true',
      pushNotifications: formData.get('pushNotifications') === 'true',
      notificationEmail: formData.get('notificationEmail'),
    };
    // Removed updateNotificationMutation call
    toast.info('Notification settings update not implemented');
  };

  const handleSMSSettingsSubmit = async (e) => {
    e.preventDefault();
    try {
      const formData = new FormData(e.target);
      const data = {
        provider: formData.get('provider'),
        apiKey: formData.get('apiKey'),
        apiSecret: formData.get('apiSecret'),
        senderId: formData.get('senderId'),
        templateId: formData.get('templateId'),
      };
      
      await configAPI.updateSMSConfig(data);
      toast.success('SMS settings updated successfully');
      fetchConfig();
    } catch (error) {
      console.error('Error updating SMS config:', error);
      toast.error('Failed to update SMS settings');
    }
  };

  const handleDLTTemplatesSubmit = async (e) => {
    e.preventDefault();
    try {
      const formData = new FormData(e.target);
      const templates = [];
      
      // Parse templates from form
      const templateNames = formData.getAll('templateName');
      const templateContents = formData.getAll('templateContent');
      const templateIds = formData.getAll('templateId');
      
      templateNames.forEach((name, index) => {
        if (name && templateContents[index]) {
          templates.push({
            name: name,
            content: templateContents[index],
            id: templateIds[index] || ''
          });
        }
      });
      
      const data = { templates };
      
      await configAPI.updateDLTConfig(data);
      toast.success('DLT templates updated successfully');
      fetchConfig();
    } catch (error) {
      console.error('Error updating DLT config:', error);
      toast.error('Failed to update DLT templates');
    }
  };

  const handleLeadStatusesSubmit = (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const leadStatuses = [
      {
        name: formData.get('status1Name'),
        color: formData.get('status1Color'),
        description: formData.get('status1Description'),
      },
      {
        name: formData.get('status2Name'),
        color: formData.get('status2Color'),
        description: formData.get('status2Description'),
      },
      {
        name: formData.get('status3Name'),
        color: formData.get('status3Color'),
        description: formData.get('status3Description'),
      },
    ];
    // Removed updateLeadStatusesMutation call
    toast.info('Lead statuses update not implemented');
  };

  const handleForceUpdateSubmit = async (e) => {
    e.preventDefault();
    try {
      setForceUpdateLoading(true);
      await configAPI.updateForceUpdate(forceUpdate);
      toast.success('Force update configuration saved');
      fetchConfig();
    } catch (error) {
      toast.error('Failed to save force update configuration');
    } finally {
      setForceUpdateLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const tabs = [
    { id: 'splash', name: 'Splash Screen' },
    { id: 'loan-form', name: 'Loan Form' },
    { id: 'contact', name: 'Contact' },
    { id: 'legal', name: 'Legal Pages' },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Configuration</h1>
        <p className="text-gray-600">Manage app configuration and settings</p>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === tab.id
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {tab.name}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="bg-white rounded-lg shadow p-6">
        {activeTab === 'splash' && (
          <form onSubmit={handleSplashSubmit} className="space-y-4">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Splash Screen Configuration</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">App Name</label>
                <input
                  type="text"
                  name="appName"
                  defaultValue={config?.splash?.appName || 'Chitti Finserv'}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Tagline</label>
                <input
                  type="text"
                  name="tagline"
                  defaultValue={config?.splash?.tagline || 'Your Trusted Financial Partner'}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Logo</label>
                <ImageUpload
                  currentImage={splashLogo}
                  onImageChange={setSplashLogo}
                  accept="image/*"
                  maxSize={2 * 1024 * 1024}
                  placeholder="Upload splash logo"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Background Color</label>
                <input
                  type="color"
                  name="backgroundColor"
                  defaultValue={config?.splash?.backgroundColor || '#3B82F6'}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Text Color</label>
                <input
                  type="color"
                  name="textColor"
                  defaultValue={config?.splash?.textColor || '#FFFFFF'}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Duration (seconds)</label>
                <input
                  type="number"
                  name="duration"
                  defaultValue={config?.splash?.duration || 3}
                  min="1"
                  max="10"
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>
            <div className="flex justify-end">
              <button
                type="submit"
                disabled={loading}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 disabled:opacity-50"
              >
                {loading ? 'Updating...' : 'Update Splash'}
              </button>
            </div>
          </form>
        )}

        {activeTab === 'loan-form' && (
          <form onSubmit={handleLoanFormSubmit} className="space-y-4">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Loan Form Configuration</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Form Title</label>
                <input
                  type="text"
                  name="formTitle"
                  defaultValue={config?.loanForm?.formTitle || 'Loan Application'}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Form Subtitle</label>
                <input
                  type="text"
                  name="formSubtitle"
                  defaultValue={config?.loanForm?.formSubtitle || 'Fill in your details to apply for a loan'}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Enable Document Upload</label>
                <select
                  name="enableDocuments"
                  defaultValue={config?.loanForm?.enableDocuments?.toString() || 'true'}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="true">Yes</option>
                  <option value="false">No</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Enable Terms Acceptance</label>
                <select
                  name="enableTerms"
                  defaultValue={config?.loanForm?.enableTerms?.toString() || 'true'}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="true">Yes</option>
                  <option value="false">No</option>
                </select>
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700">Required Fields</label>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-2">
                  {['name', 'email', 'phone', 'address', 'income', 'employment', 'loanAmount', 'purpose'].map(field => (
                    <label key={field} className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        name={`required_${field}`}
                        defaultChecked={config?.loanForm?.requiredFields?.includes(field) || false}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="text-sm text-gray-700 capitalize">{field}</span>
                    </label>
                  ))}
                </div>
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700">Loan Categories</label>
                <textarea
                  name="categories"
                  defaultValue={config?.loanForm?.categories || 'Personal Loan,Home Loan,Business Loan,Education Loan,Vehicle Loan'}
                  rows={3}
                  placeholder="Enter categories separated by commas"
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>
            <div className="flex justify-end">
              <button
                type="submit"
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700"
              >
                Update Loan Form
              </button>
            </div>
          </form>
        )}

        {activeTab === 'contact' && (
          <form onSubmit={handleContactSubmit} className="space-y-4">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Contact Configuration</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Company Name</label>
                <input
                  type="text"
                  name="companyName"
                  defaultValue={config?.contactInfo?.companyName || 'Chitti Finserv'}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Email</label>
                <input
                  type="email"
                  name="email"
                  defaultValue={config?.contactInfo?.email || 'info@chittifinserv.com'}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Phone</label>
                <input
                  type="tel"
                  name="phone"
                  defaultValue={config?.contactInfo?.phone || '+91 9876543210'}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">WhatsApp</label>
                <input
                  type="tel"
                  name="whatsapp"
                  defaultValue={config?.contactInfo?.whatsapp || '+91 9876543210'}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Address</label>
                <textarea
                  name="address"
                  defaultValue={config?.contactInfo?.address || 'Mumbai, Maharashtra, India'}
                  rows={3}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Working Hours</label>
                <input
                  type="text"
                  name="workingHours"
                  defaultValue={config?.contactInfo?.workingHours || 'Mon-Fri: 9AM-6PM'}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Website</label>
                <input
                  type="url"
                  name="website"
                  defaultValue={config?.contactInfo?.website || 'https://chittifinserv.com'}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Facebook</label>
                <input
                  type="url"
                  name="facebook"
                  defaultValue={config?.contactInfo.socialMedia?.facebook || ''}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Instagram</label>
                <input
                  type="url"
                  name="instagram"
                  defaultValue={config?.contactInfo.socialMedia?.instagram || ''}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Twitter</label>
                <input
                  type="url"
                  name="twitter"
                  defaultValue={config?.contactInfo.socialMedia?.twitter || ''}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">LinkedIn</label>
                <input
                  type="url"
                  name="linkedin"
                  defaultValue={config?.contactInfo?.socialMedia?.linkedin || ''}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>
            <div className="flex justify-end">
              <button
                type="submit"
                disabled={loading}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 disabled:opacity-50"
              >
                {loading ? 'Updating...' : 'Update Contact'}
              </button>
            </div>
          </form>
        )}

        {activeTab === 'legal' && (
          <form onSubmit={handleLegalSubmit} className="space-y-4">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Legal Pages</h3>
            <div className="space-y-4">
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">Terms & Conditions (HTML)</label>
                <ReactQuill
                  theme="snow"
                  value={legal.termsConditions}
                  onChange={value => setLegal(l => ({ ...l, termsConditions: value }))}
                  className="bg-white rounded-lg border border-gray-300"
                  style={{ minHeight: 120 }}
                />
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">Privacy Policy (HTML)</label>
                <ReactQuill
                  theme="snow"
                  value={legal.privacyPolicy}
                  onChange={value => setLegal(l => ({ ...l, privacyPolicy: value }))}
                  className="bg-white rounded-lg border border-gray-300"
                  style={{ minHeight: 120 }}
                />
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">Data Safety (HTML)</label>
                <ReactQuill
                  theme="snow"
                  value={legal.dataSafety}
                  onChange={value => setLegal(l => ({ ...l, dataSafety: value }))}
                  className="bg-white rounded-lg border border-gray-300"
                  style={{ minHeight: 120 }}
                />
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">Disclaimer (HTML)</label>
                <ReactQuill
                  theme="snow"
                  value={legal.disclaimer}
                  onChange={value => setLegal(l => ({ ...l, disclaimer: value }))}
                  className="bg-white rounded-lg border border-gray-300"
                  style={{ minHeight: 120 }}
                />
              </div>
            </div>
            <div className="flex justify-end">
              <button
                type="submit"
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700"
              >
                Update Legal Pages
              </button>
            </div>
          </form>
        )}

      </div>
      {/* Force Update Section */}
      <div className="mt-12 max-w-xl mx-auto bg-white rounded-xl shadow-lg border border-blue-100 p-8">
        <h2 className="text-2xl font-bold mb-6 text-blue-700 flex items-center gap-2">
          <svg className="w-6 h-6 text-blue-500" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M13 16h-1v-4h-1m1-4h.01M12 20.5C7.305 20.5 3.5 16.695 3.5 12S7.305 3.5 12 3.5 20.5 7.305 20.5 12 16.695 20.5 12 20.5z" /></svg>
          Force Update
        </h2>
        <form onSubmit={handleForceUpdateSubmit} className="space-y-6">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">Minimum Required Version</label>
            <input
              type="text"
              className="mt-1 block w-full border border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400 transition"
              value={forceUpdate.version}
              onChange={e => setForceUpdate({ ...forceUpdate, version: e.target.value })}
              placeholder="e.g. 1.0.0"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">Update Message</label>
            <textarea
              className="mt-1 block w-full border border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400 transition"
              value={forceUpdate.message}
              onChange={e => setForceUpdate({ ...forceUpdate, message: e.target.value })}
              placeholder="A new version is available. Please update to continue."
              rows={2}
              required
            />
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">Android Update URL</label>
            <input
              type="text"
              className="mt-1 block w-full border border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400 transition"
              value={forceUpdate.androidUrl || ''}
              onChange={e => setForceUpdate({ ...forceUpdate, androidUrl: e.target.value })}
              placeholder="https://play.google.com/store/apps/details?id=..."
            />
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">iOS Update URL</label>
            <input
              type="text"
              className="mt-1 block w-full border border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400 transition"
              value={forceUpdate.iosUrl || ''}
              onChange={e => setForceUpdate({ ...forceUpdate, iosUrl: e.target.value })}
              placeholder="https://apps.apple.com/app/id..."
            />
          </div>
          <div className="flex items-center gap-3 mt-2">
            <label htmlFor="forceUpdateActive" className="font-semibold text-gray-700">Enable Force Update</label>
            <button
              type="button"
              onClick={() => setForceUpdate({ ...forceUpdate, isActive: !forceUpdate.isActive })}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${forceUpdate.isActive ? 'bg-blue-600' : 'bg-gray-300'}`}
              aria-pressed={forceUpdate.isActive}
            >
              <span className="sr-only">Enable Force Update</span>
              <span
                className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${forceUpdate.isActive ? 'translate-x-5' : 'translate-x-1'}`}
              />
            </button>
            <span className={`text-xs font-medium ${forceUpdate.isActive ? 'text-blue-600' : 'text-gray-400'}`}>{forceUpdate.isActive ? 'Active' : 'Inactive'}</span>
          </div>
          <div className="flex justify-end pt-4">
            <button
              type="submit"
              disabled={forceUpdateLoading}
              className="px-6 py-2 text-base font-semibold text-white bg-blue-600 border border-transparent rounded-lg shadow hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-offset-2 disabled:opacity-50 transition"
            >
              {forceUpdateLoading ? 'Saving...' : 'Save Force Update'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Configuration; 